import type { ReactNode } from 'react'
import { useRef } from 'react'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import type { SxProps } from '@mui/material/styles'

import { Icon } from '@mxenabled/mx-icons'

import { Confetti } from './shared/Confetti'
import { useContainerDimensions } from '../hooks/useContainerDimensions'

// Figma track/row footprints (mirrors v1 `shared/ConnectionCard`): a 240×40
// rounded track centered behind a 204×64 row of logos + center badge.
const TRACK_WIDTH = 240
const TRACK_HEIGHT = 40
const ROW_WIDTH = 204

interface TransferLogoCardProps {
  /** Source institution logo (left). */
  from: ReactNode
  /** Destination institution logo (right). */
  to: ReactNode
  /** Logo tile size in px (Figma: 64). */
  size?: number
  /** Arrow icon rendered in the center badge. Defaults to a right arrow. */
  icon?: ReactNode
  /**
   * Enlarge the inner connector dots (6px vs 4px), matching the Discovered
   * Accounts insight for cross-insight consistency. Default true.
   */
  hasBigDots?: boolean
  /** Connector-dot color token. Default `neutral.darker` (theme-aware). */
  dotColor?: string
  /** Center badge background color token. Default `neutral.darker` (dark circle
   * for Switch DD default; set to `success.main` for Discovered Accounts success). */
  badgeBgColor?: string
  /** Slot surface color token. Default `primary.lighter` (Figma's tinted slot,
   * theme-aware: light-blue in light mode, navy in dark). */
  bgcolor?: string
  /**
   * Optional background image (e.g., celebration gradient) for success states.
   * Layered over the `bgcolor` via `backgroundImage` (not `background` shorthand).
   */
  backgroundImage?: string
  /**
   * Show a confetti animation filling the slot (for success/celebration states).
   * Requires `useContainerDimensions` to measure the slot. Default false.
   */
  showConfetti?: boolean
  sx?: SxProps
}

// Default center-badge glyph. Hoisted to a stable reference so it isn't
// re-created each render (`neutral.lighter` is the theme-aware inverse of the
// badge fill: light glyph on dark badge, and vice-versa in dark).
const defaultTransferIcon = (
  <Icon aria-hidden={true} name="arrow_forward" size={20} sx={{ color: 'neutral.lighter' }} />
)

// Connector dot: a small rounded square that reads as a dot at this size
// (matches v1 `ConnectionCard`'s `Dot`). `neutral.darker` is theme-aware. The
// inner pair is larger (`bigDots`, as Discovered Accounts uses) than the outer.
const Dot = ({ color = 'neutral.darker', size = 4 }: { color?: string; size?: number }) => (
  <Box sx={{ bgcolor: color, borderRadius: '4px', height: size, width: size }} />
)

/**
 * Confetti overlay for the success state. Measures the container and fires the
 * burst once dimensions arrive. Scoped to this component so the resize listener
 * only mounts when confetti is shown.
 */
const ConfettiOverlay = () => {
  const overlayRef = useRef<HTMLDivElement>(null)
  const { height, width } = useContainerDimensions(overlayRef)
  return (
    <Box aria-hidden={true} ref={overlayRef} sx={{ inset: 0, position: 'absolute', zIndex: 0 }}>
      <Confetti height={height} shouldRun={height > 0} width={width} />
    </Box>
  )
}

/**
 * A from → to account transfer visual: two institution logos on a rounded track
 * with a central arrow badge and connector dots between them.
 *
 * Figma: Switch Direct Deposit (default) Content. Structure ported from v1
 * `insights/components/shared/ConnectionCard` (grid-overlaid track + row).
 * Scope is the Content section only.
 */
const TransferLogoCard = ({
  badgeBgColor = 'neutral.darker',
  bgcolor = 'primary.lighter',
  backgroundImage,
  dotColor = 'neutral.darker',
  from,
  hasBigDots = true,
  icon = defaultTransferIcon,
  showConfetti = false,
  size = 64,
  to,
  sx,
}: TransferLogoCardProps) => {
  const dotSize = hasBigDots ? 6 : 4
  const logoSx = {
    borderRadius: '4px',
    flexShrink: 0,
    height: size,
    overflow: 'hidden',
    width: size,
  } as const

  return (
    <Box
      sx={{
        bgcolor,
        ...(backgroundImage && { backgroundImage }),
        p: 16,
        // Positioned wrapper so the confetti overlay can fill the slot; the card
        // sits above it (zIndex). No `overflow: hidden` — that would clip the
        // burst's upper area (per the figma-to-code confetti guidance).
        position: 'relative',
        width: '100%',
        ...sx,
      }}
    >
      {showConfetti && <ConfettiOverlay />}
      <Card sx={{ position: 'relative', py: 16, zIndex: 1 }}>
        <Box sx={{ display: 'grid', placeItems: 'center' }}>
          {/* Grey track, centered behind the row via a shared grid cell. */}
          <Box
            sx={{
              bgcolor: 'neutral.lighter',
              borderRadius: '24px',
              gridArea: '1 / 1',
              height: TRACK_HEIGHT,
              width: TRACK_WIDTH,
            }}
          />
          <Stack
            sx={{
              alignItems: 'center',
              flexDirection: 'row',
              gridArea: '1 / 1',
              height: size,
              justifyContent: 'space-between',
              width: ROW_WIDTH,
            }}
          >
            <Box sx={logoSx}>{from}</Box>
            <Stack sx={{ alignItems: 'center', flexDirection: 'row', gap: 4 }}>
              <Dot color={dotColor} />
              <Dot color={dotColor} size={dotSize} />
              <Stack
                sx={{
                  alignItems: 'center',
                  bgcolor: badgeBgColor,
                  borderRadius: '50%',
                  height: 28,
                  justifyContent: 'center',
                  width: 28,
                }}
              >
                {icon}
              </Stack>
              <Dot color={dotColor} size={dotSize} />
              <Dot color={dotColor} />
            </Stack>
            <Box sx={logoSx}>{to}</Box>
          </Stack>
        </Box>
      </Card>
    </Box>
  )
}

export default TransferLogoCard
